import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { Avatar } from '../components/Avatar';
import { VerifiedBadge } from '../components/Badges';
import { Button, ButtonLink } from '../components/Button';
import { TextArea, TextField } from '../components/Field';
import { Icon } from '../components/Icon';
import { Paper } from '../components/Paper';
import { ErrorNotice } from '../components/States';
import { useToast } from '../components/Toast';
import { meQueryKey, signOut, useMe, useSession } from '../features/auth/session';
import { useRefreshBoards } from '../features/feed/queries';
import { DeleteAccount } from '../features/profile/DeleteAccount';
import { listMyBlocks, unblockUser, updateMyProfile, type Me as MeData } from '../lib/api';
import { monthYear, shortDate } from '../lib/time';
import page from './Page.module.css';
import styles from './Me.module.css';

export function Me() {
  const { data: me } = useMe();
  const { session } = useSession();
  const [editing, setEditing] = useState(false);
  if (!me) return null;
  const isMod = me.role === 'moderator' || me.role === 'admin';

  return (
    <div className={page.stack}>
      <h1 className={page.pageTitle}>Me</h1>

      {me.status === 'suspended' && me.suspendedUntil && (
        <p className={styles.notice} role="status">
          Your account is suspended until {shortDate(me.suspendedUntil)}
          {me.statusReason ? `: “${me.statusReason}”` : '.'} You can still read your board.
        </p>
      )}

      <Paper className={styles.identity}>
        <Avatar name={me.displayName} hue={me.avatarHue} size={64} />
        <div>
          <h2 className={styles.name}>{me.displayName}</h2>
          <div className={styles.facts}>
            {me.location && <VerifiedBadge />}
            {me.location && <span>{me.location.placeName}</span>}
            <span>Joined {monthYear(me.createdAt)}</span>
          </div>
        </div>
        {!editing && (
          <Button size="small" onClick={() => setEditing(true)}>
            Edit
          </Button>
        )}
        {editing ? (
          <EditProfile me={me} onDone={() => setEditing(false)} />
        ) : (
          me.bio && <p className={styles.bio}>{me.bio}</p>
        )}
      </Paper>

      <Paper as="section" className={styles.panel} aria-labelledby="home-area">
        <h2 id="home-area" className={styles.panelTitle}>
          Home area
        </h2>
        {me.location ? (
          <>
            <p>
              You're a verified local in <strong>{me.location.placeName}</strong> since{' '}
              {shortDate(me.location.verifiedAt)}.{me.location.simulated && ' (Simulated location, development only.)'}
            </p>
            <div className={styles.row}>
              <span className={styles.muted}>
                {new Date(me.location.nextChangeAt) > new Date()
                  ? `You can move your home area after ${shortDate(me.location.nextChangeAt)}.`
                  : 'Moved? You can verify a new home area.'}
              </span>
              <ButtonLink to="/me/location" size="small">
                <Icon name="pin" size={16} />
                Move my home area
              </ButtonLink>
            </div>
          </>
        ) : (
          <div className={styles.row}>
            <span>You haven't verified a home area yet.</span>
            <ButtonLink to="/me/location" size="small" variant="primary">
              Verify my location
            </ButtonLink>
          </div>
        )}
      </Paper>

      <BlockedNeighbors />

      <Paper as="section" className={styles.panel} aria-labelledby="privacy">
        <h2 id="privacy" className={styles.panelTitle}>
          What Roadrunner knows about you
        </h2>
        <ul className={styles.privacy}>
          <li>
            Your email address{session?.user.email ? ` (${session.user.email})` : ''}, used only to sign you in.
            Neighbors never see it.
          </li>
          <li>Your name and bio, which neighbors see.</li>
          <li>
            A rough home area about the size of a few city blocks. It decides whose notes you see and is never shown to
            anyone. We don't keep your exact coordinates.
          </li>
          <li>The notes, replies, thanks, reports and blocks you make.</li>
        </ul>
      </Paper>

      {isMod && (
        <Paper as="section" className={styles.panel} aria-labelledby="mod-tools">
          <h2 id="mod-tools" className={styles.panelTitle}>
            Moderator tools
          </h2>
          <div className={styles.row}>
            <ButtonLink to="/mod" size="small">
              <Icon name="shield" size={16} />
              Moderation queue
            </ButtonLink>
            {me.role === 'admin' && (
              <ButtonLink to="/admin/geo" size="small">
                <Icon name="map" size={16} />
                Geo debug
              </ButtonLink>
            )}
          </div>
        </Paper>
      )}

      <DeleteAccount />

      <div className={styles.row}>
        <Button onClick={() => signOut()}>
          <Icon name="logout" size={18} />
          Sign out
        </Button>
        <span className={styles.links}>
          <Link to="/about">About</Link>
          <Link to="/privacy">Privacy</Link>
          <Link to="/guidelines">Guidelines</Link>
          <Link to="/terms">Terms</Link>
        </span>
      </div>
    </div>
  );
}

function EditProfile({ me, onDone }: { me: MeData; onDone: () => void }) {
  const [name, setName] = useState(me.displayName ?? '');
  const [bio, setBio] = useState(me.bio);
  const queryClient = useQueryClient();
  const refresh = useRefreshBoards();
  const toast = useToast();
  const save = useMutation({
    mutationFn: () => updateMyProfile(name, bio),
    onSuccess: async () => {
      toast.show('Profile saved');
      await queryClient.invalidateQueries({ queryKey: meQueryKey });
      await refresh();
      onDone();
    },
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate();
  };
  return (
    <form className={styles.form} onSubmit={submit}>
      <TextField
        label="Name"
        required
        minLength={2}
        maxLength={40}
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <TextArea
        label="About you (optional)"
        hint="A line or two, like how long you've lived here or what you can help with."
        value={bio}
        maxLength={280}
        rows={3}
        onChange={(e) => setBio(e.target.value)}
      />
      {save.error && <ErrorNotice error={save.error} />}
      <div className={styles.formButtons}>
        <Button variant="quiet" onClick={onDone}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabled={save.isPending || name.trim().length < 2}>
          Save profile
        </Button>
      </div>
    </form>
  );
}

function BlockedNeighbors() {
  const blocks = useQuery({ queryKey: ['blocks'], queryFn: listMyBlocks });
  const queryClient = useQueryClient();
  const refresh = useRefreshBoards();
  const unblock = useMutation({
    mutationFn: (userId: string) => unblockUser(userId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['blocks'] });
      await refresh();
    },
  });

  if (!blocks.data || blocks.data.length === 0) return null;

  return (
    <Paper as="section" className={styles.panel} aria-labelledby="blocked">
      <h2 id="blocked" className={styles.panelTitle}>
        Blocked
      </h2>
      <ul className={styles.list}>
        {blocks.data.map((b) => (
          <li key={b.user_id} className={styles.listItem}>
            <Avatar name={b.display_name} hue={b.avatar_hue} size={32} />
            <span>{b.display_name ?? 'Unnamed neighbor'}</span>
            <Button size="small" onClick={() => unblock.mutate(b.user_id)} disabled={unblock.isPending}>
              Unblock
            </Button>
          </li>
        ))}
      </ul>
    </Paper>
  );
}
